import React, { useEffect, useState } from "react";
import { I18n } from "@aws-amplify/core";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/styles";
import update from "immutability-helper";
import {
  get,
  map,
  includes,
  filter,
  isEmpty,
  forEach,
  sortBy,
  toLower,
} from "lodash";

import { fetchGet } from "../../api/fetch";

import TableRow from "@material-ui/core/TableRow";

import ScrollableTable from "../Common/Table/ScrollableTable";
import TableCellStyled from "../Common/TableCellStyled";
import TagColor from "../Common/TagColor";
import UserModal from "./UserModal";
import UsersAndRoles from "./UsersAndRoles";

import { formatDate } from "../../utils/date";
import { getLov } from "../../utils/lov";

import colors from "../../assets/style/colors";
import { booleanSelect } from "../../constants/filters";

const columns = [
  {
    name: "profiles_firstName",
    minWidth: 150,
    width: 150,
    filter: {
      type: "smartsearch",
      name: "firstname",
      path: ["profile", "firstname"],
    },
  },
  {
    name: "profiles_lastName",
    minWidth: 150,
    width: 150,
    filter: {
      type: "smartsearch",
      name: "lastname",
      path: ["profile", "lastname"],
    },
  },
  {
    name: "profiles_userRole",
    minWidth: 150,
    filter: {
      type: "select",
      name: "role",
    },
  },
  {
    name: "profiles_active",
    minWidth: 100,
    filter: {
      type: "select",
      name: "active",
    },
  },
  {
    name: "profiles_username",
    minWidth: 150,
    filter: {
      type: "smartsearch",
      name: "username",
      path: ["username"],
    },
  },
  {
    name: "profiles_email",
    minWidth: 250,
    filter: {
      type: "smartsearch",
      name: "email",
      path: ["profile", "email"],
    },
  },
  {
    name: "profiles_tag",
    minWidth: 150,
    filter: {
      type: "select",
      name: "tag",
    },
  },
  {
    name: "profiles_createdOn",
    minWidth: 200,
  },
  {
    name: "site_lastModified",
    minWidth: 200,
  },
];

const styles = {
  row: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: `${colors.lightGreenBg} !important`,
    },
  },
};

const Users = (props) => {
  const { classes, sensitive = false } = props;

  const [users, setUsers] = useState();
  const [isFetching, setIsFetching] = useState();
  const [filters, setFilters] = useState({
    tag: [],
    role: [],
    active: [],
  });
  const [lov, setLov] = useState();
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchLov();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      setIsFetching(true);
      let users = await fetchGet("users");
      setUsers(users);
    } catch (error) {
      console.error(error);
    }
    setIsFetching(false);
  };

  const fetchLov = async () => {
    try {
      const tags = await getLov("tags");
      const roles = await getLov("roles");
      setLov({
        tag: tags,
        role: roles,
        active: booleanSelect,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleFilterChange = (value, filterName) => {
    if (includes(value, "selectAll")) {
      value = map(get(lov, [filterName]), (opt) => opt.id);
    } else if (includes(value, "selectNone")) {
      value = [];
    }

    setFilters(
      update(filters, {
        [filterName]: { $set: value },
      })
    );
  };

  const applyFilters = (item) => {
    let exclude = false;
    if (!isEmpty(get(filters, ["role"]))) {
      if (isEmpty(get(item, ["roles"]))) exclude = true;
      forEach(get(item, ["roles"]), (r) => {
        if (!includes(get(filters, ["role"]), get(r, ["id"]))) {
          exclude = true;
        }
      });
    }

    if (
      !isEmpty(get(filters, ["tag"])) &&
      !includes(get(filters, ["tag"]), get(item, ["tag", "id"]))
    ) {
      exclude = true;
    }

    if (
      !isEmpty(get(filters, ["active"])) &&
      !includes(get(filters, ["active"]), get(item, ["active"]))
    ) {
      exclude = true;
    }

    forEach(columns, (v, k) => {
      if (
        includes(
          ["firstname", "lastname", "username", "email"],
          get(columns, [k, "filter", "name"])
        ) &&
        !isEmpty(get(filters, [get(columns, [k, "filter", "name"])])) &&
        get(filters, [get(columns, [k, "filter", "name"])]) !==
          get(item, get(columns, [k, "filter", "path"]))
      ) {
        exclude = true;
      }
    });

    return !exclude;
  };

  const renderDataCell = (data) => (
    <TableCellStyled className="text-54">{data || "-"}</TableCellStyled>
  );

  return (
    <UsersAndRoles
      url={get(props, ["match", "url"])}
      history={props.history}
      topElement={
        <UserModal
          onClose={fetchUsers}
          isOpen={isUserModalOpen}
          setIsOpen={setIsUserModalOpen}
        />
      }
    >
      <ScrollableTable
        columns={columns}
        hasMore={false}
        isFetching={isFetching}
        stickyHeader
        filters={filters}
        lov={lov}
        handleFilterChange={handleFilterChange}
        data={users}
      >
        {map(
          sortBy(filter(users, applyFilters), (usr) =>
            toLower(get(usr, ["profile", "firstname"]))
          ),
          (usr, index) => (
            <TableRow
              key={usr.id}
              onClick={() => props.history.push(`users/${get(usr, ["id"])}`)}
              className={classes.row}
              style={{
                backgroundColor:
                  index % 2 === 1 ? colors.tableDarkBg : colors.white,
              }}
            >
              {sensitive
                ? renderDataCell("********")
                : renderDataCell(get(usr, ["profile", "firstname"]))}
              {sensitive
                ? renderDataCell("********")
                : renderDataCell(get(usr, ["profile", "lastname"]))}
              {renderDataCell(get(usr, ["roles", 0, "name", props.language]))}
              {renderDataCell(
                get(usr, ["active"])
                  ? I18n.get("profiles_yes")
                  : I18n.get("profiles_no")
              )}
              {renderDataCell(get(usr, ["username"]))}
              {renderDataCell(get(usr, ["profile", "email"]))}
              <TableCellStyled>
                {get(usr, ["tag", "name", props.language]) ? (
                  <TagColor
                    color={get(usr, ["tag", "color", "rgb"])}
                    name={get(usr, ["tag", "name", props.language])}
                  />
                ) : (
                  <span className="text-54">-</span>
                )}
              </TableCellStyled>
              {renderDataCell(formatDate(get(usr, ["created_on"])))}
              {renderDataCell(formatDate(get(usr, ["updated_on"])))}
            </TableRow>
          )
        )}
      </ScrollableTable>
    </UsersAndRoles>
  );
};

const mapStateToProps = (state) => {
  return {
    language: state.user.language,
  };
};

export default connect(mapStateToProps)(withStyles(styles)(Users));
